import { supabase } from '../lib/supabase'
import type {
  ComercialActivity,
  ComercialActivityInput,
  ComercialActivityResponsible,
} from '../types/comercialAtividades'

const ACTIVITY_SELECT = `
  *,
  broker:brokers(id, name, id_scgp),
  activity_type:helpdesk_commercial_activity_types(id, name),
  visit_objective:helpdesk_commercial_objective_contact(id, name)
`

function normalizeDestinationFields(input: ComercialActivityInput) {
  const visitedLocation = input.visited_location?.trim() || null
  const brokerId = input.broker_id || null
  return {
    broker_id: brokerId,
    visited_location: brokerId ? null : visitedLocation,
  }
}

export const comercialAtividadesService = {
  async listActivities(): Promise<ComercialActivity[]> {
    const pageSize = 1000
    let page = 0
    let hasMore = true
    const allRows: ComercialActivity[] = []

    while (hasMore) {
      const from = page * pageSize
      const to = from + pageSize - 1
      const { data, error } = await supabase
        .from('helpdesk_commercial_activities')
        .select(ACTIVITY_SELECT)
        .is('deleted_at', null)
        .order('activity_date', { ascending: false })
        .range(from, to)

      if (error) throw error

      const batch = (data ?? []) as ComercialActivity[]
      allRows.push(...batch)

      if (batch.length < pageSize) {
        hasMore = false
      } else {
        page += 1
      }
    }

    return allRows
  },

  async createActivity(
    input: ComercialActivityInput,
    responsible: ComercialActivityResponsible,
  ): Promise<void> {
    const destination = normalizeDestinationFields(input)
    const { error } = await supabase.from('helpdesk_commercial_activities').insert({
      commercial_responsible_user_id: responsible.userId,
      commercial_responsible_name: responsible.name,
      activity_date: input.activity_date,
      broker_id: destination.broker_id,
      visited_location: destination.visited_location,
      activity_type_id: input.activity_type_id,
      visit_objective_id: input.visit_objective_id,
      overall_feedback: input.overall_feedback,
      general_perceptions: input.general_perceptions.trim(),
    })

    if (error) throw error
  },

  async updateActivity(id: string, input: ComercialActivityInput): Promise<void> {
    const destination = normalizeDestinationFields(input)
    const { error } = await supabase
      .from('helpdesk_commercial_activities')
      .update({
        activity_date: input.activity_date,
        broker_id: destination.broker_id,
        visited_location: destination.visited_location,
        activity_type_id: input.activity_type_id,
        visit_objective_id: input.visit_objective_id,
        overall_feedback: input.overall_feedback,
        general_perceptions: input.general_perceptions.trim(),
      })
      .eq('id', id)

    if (error) throw error
  },

  async softDeleteActivity(id: string): Promise<void> {
    const { error } = await supabase
      .from('helpdesk_commercial_activities')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)

    if (error) throw error
  },
}
